import React from "react";
import { InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { AccountTreeOutlined, Add, Download, Search } from "@mui/icons-material";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import { SLA_DIAGRAM_TEXT_KEYS } from "model/slaDiagramModel";

interface ProcessLibraryToolbarProps {
  isAdmin?: boolean;
  searchText: string;
  searchCount: number;
  searchIndex: number;
  onSearchChange: (value: string) => void;
  onPreviousSearchResult: () => void;
  onNextSearchResult: () => void;
  onCreateModel: () => void;
  onCreateFolder: () => void;
  onImportModel: () => void;
  /** Apre il Diagramma SLA (`/dashboard/process/sla-diagram`). */
  onOpenSlaDiagram: () => void;
}

const ProcessLibraryToolbar: React.FC<ProcessLibraryToolbarProps> = ({
  isAdmin,
  searchText,
  searchCount,
  searchIndex,
  onSearchChange,
  onPreviousSearchResult,
  onNextSearchResult,
  onCreateModel,
  onCreateFolder,
  onImportModel,
  onOpenSlaDiagram,
}) => {
  const translations = useTranslations();

  return (
  <Stack
    direction={{ xs: "column", lg: "row" }}
    spacing={2}
    alignItems={{ xs: "stretch", lg: "center" }}
    justifyContent="space-between"
  >
    <Stack direction="row" spacing={1.5} sx={{ "& > *": { flex: { xs: 1, sm: "initial" } } }}>
      {isAdmin && (
        <>
        <FlowentiButton
          id="create-new-process-button"
          variant="outlined"
          startIcon={<Add />}
          onClick={onCreateModel}
          sx={{
            minHeight: 38,
            minWidth: 0,
            whiteSpace: "nowrap",
            fontSize: { xs: "0.7rem", sm: "0.875rem" },
            px: { xs: 1, sm: 3 },
            "& .MuiButton-startIcon": { mr: { xs: 0.5, sm: 1 } },
          }}
        >
          {getTranslation(translations, "pm.label.button.process_name")}
        </FlowentiButton>
        <FlowentiButton
          id="create-new-folder-button"
          variant="outlined"
          startIcon={<Add />}
          onClick={onCreateFolder}
          sx={{
            minHeight: 38,
            minWidth: 0,
            whiteSpace: "nowrap",
            fontSize: { xs: "0.7rem", sm: "0.875rem" },
            px: { xs: 1, sm: 3 },
            "& .MuiButton-startIcon": { mr: { xs: 0.5, sm: 1 } },
          }}
        >
          {getTranslation(translations, "pm.label.button.newfolder")}
        </FlowentiButton>
        <FlowentiButton
          id="import-model"
          variant="outlined"
          startIcon={<Download />}
          onClick={onImportModel}
          sx={{
            minHeight: 38,
            minWidth: 0,
            whiteSpace: "nowrap",
            fontSize: { xs: "0.7rem", sm: "0.875rem" },
            px: { xs: 1, sm: 3 },
            "& .MuiButton-startIcon": { mr: { xs: 0.5, sm: 1 } },
          }}
        >
          {getTranslation(translations, "pm.button.import_model")}
        </FlowentiButton>
        </>
      )}
      {/* Diagramma SLA: fuori dalla guardia admin, come la vecchia voce di
          sidebar (visibile a tutti; la superficie gestisce i propri errori).
          Etichetta dalla chiave del manifest (`sla.diagram.title`). */}
      <FlowentiButton
        id="open-sla-diagram"
        variant="outlined"
        startIcon={<AccountTreeOutlined />}
        onClick={onOpenSlaDiagram}
        sx={{
          minHeight: 38,
          minWidth: 0,
          whiteSpace: "nowrap",
          fontSize: { xs: "0.7rem", sm: "0.875rem" },
          px: { xs: 1, sm: 3 },
          "& .MuiButton-startIcon": { mr: { xs: 0.5, sm: 1 } },
        }}
      >
        {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.title)}
      </FlowentiButton>
    </Stack>

    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      justifyContent={{ xs: "space-between", lg: "flex-end" }}
      flex={1}
    >
      <TextField
        id="search-bar-model"
        size="small"
        value={searchText}
        onChange={({ target }) => onSearchChange(target.value)}
        placeholder={getTranslation(translations, "process_library.search_models")}
        sx={{
          minWidth: { xs: 0, sm: 320 },
          maxWidth: 390,
          flex: { xs: 1, lg: "initial" },
          "& .MuiOutlinedInput-root": {
            borderRadius: "999px",
            bgcolor: "action.hover",
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search />
            </InputAdornment>
          ),
        }}
      />
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2" fontWeight={700}>
          {searchCount ? searchIndex + 1 : 0}/{searchCount}
        </Typography>
      </Stack>
    </Stack>
  </Stack>
  );
};

export default ProcessLibraryToolbar;
